// HumanInterviewPanel — full-screen HR view during / after a human interview.
// Route: /jobs/:jobId/human-interview/:stageId/panel/:resumeId
//
// Layout: CV summary (left) | Questionnaire answers (right) | Notes + decision (bottom)

import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Loader2, User, Briefcase, GraduationCap, Star,
  Video, CheckCircle, XCircle, Clock, Save, ArrowLeft,
  FileText, MessageSquare, Award,
} from "lucide-react";
import api from "../services/api";
import { toast } from "react-toastify";

const DECISION_CONFIG = {
  hire:   { label: "Hire",   color: "bg-green-500 hover:bg-green-600",  icon: CheckCircle, badge: "bg-green-100 text-green-800" },
  hold:   { label: "Hold",   color: "bg-amber-500 hover:bg-amber-600",  icon: Clock,        badge: "bg-amber-100 text-amber-800"  },
  reject: { label: "Reject", color: "bg-red-500 hover:bg-red-600",     icon: XCircle,      badge: "bg-red-100 text-red-800"      },
};

export default function HumanInterviewPanel() {
  const { jobId, stageId, resumeId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [data, setData]         = useState(null);
  const [notes, setNotes]       = useState("");
  const [decision, setDecision] = useState(null); // 'hire'|'hold'|'reject'|null
  const [saved, setSaved]       = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/human-interview/${jobId}/${stageId}/panel/${resumeId}`);
      const d = r.data?.data;
      setData(d);
      setNotes(d?.interviewStage?.hrNotes || "");
      setDecision(d?.interviewStage?.hrDecision || null);
      if (d?.interviewStage?.hrDecision) setSaved(true);
    } catch {
      toast.error("Could not load interview panel");
    } finally {
      setLoading(false);
    }
  }, [jobId, stageId, resumeId]);

  useEffect(() => { load(); }, [load]);

  const saveDecision = async () => {
    if (!decision) { toast.warn("Please select a decision before saving."); return; }
    setSaving(true);
    try {
      await api.post(`/human-interview/${jobId}/${stageId}/decision/${resumeId}`, { decision, notes });
      toast.success("Decision saved");
      setSaved(true);
    } catch {
      toast.error("Failed to save decision");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
    </div>
  );

  const { cvSummary: cv, questionnaireData, interviewStage } = data || {};
  const currentDecision = DECISION_CONFIG[decision];

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Top bar */}
      <div className="bg-white border-b px-6 py-3 flex items-center justify-between shadow-sm">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-gray-400 hover:text-gray-700">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="font-bold text-gray-900 text-base leading-tight">Interview Panel</h1>
            <p className="text-xs text-gray-400">{data?.jobTitle} · {cv?.name}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {interviewStage?.meetLink && (
            <a
              href={interviewStage.meetLink}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2 bg-blue-600 text-white text-sm px-4 py-2 rounded-lg hover:bg-blue-700"
            >
              <Video className="w-4 h-4" /> Open Google Meet
            </a>
          )}
          {saved && decision && (
            <span className={`text-xs font-semibold px-3 py-1.5 rounded-full ${DECISION_CONFIG[decision]?.badge}`}>
              Decision: {DECISION_CONFIG[decision]?.label}
            </span>
          )}
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-4 p-4 overflow-auto">
        {/* LEFT — CV Summary */}
        <div className="space-y-4">
          {/* Identity */}
          <div className="bg-white rounded-2xl border shadow-sm p-5">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                <User className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h2 className="font-bold text-gray-900 text-lg">{cv?.name}</h2>
                <p className="text-sm text-gray-500">{cv?.email}</p>
                {cv?.phone && <p className="text-sm text-gray-500">{cv?.phone}</p>}
              </div>
            </div>
            <div className="flex gap-3 flex-wrap">
              {cv?.atsScore !== undefined && (
                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-lg">
                  ATS: <strong>{cv.atsScore}%</strong>
                </span>
              )}
              {cv?.jdMatch !== undefined && (
                <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded-lg">
                  JD Match: <strong>{cv.jdMatch}%</strong>
                </span>
              )}
            </div>
            {cv?.summary && (
              <p className="mt-3 text-sm text-gray-600 leading-relaxed border-t pt-3">{cv.summary}</p>
            )}
          </div>

          {/* Skills */}
          {cv?.skills?.length > 0 && (
            <div className="bg-white rounded-2xl border shadow-sm p-5">
              <h3 className="font-semibold text-gray-700 text-sm mb-3 flex items-center gap-2">
                <Star className="w-4 h-4 text-amber-400" /> Skills
              </h3>
              <div className="flex flex-wrap gap-2">
                {cv.skills.slice(0, 20).map((s, i) => (
                  <span key={i} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-lg">{s}</span>
                ))}
              </div>
            </div>
          )}

          {/* Experience */}
          {cv?.experience?.length > 0 && (
            <div className="bg-white rounded-2xl border shadow-sm p-5">
              <h3 className="font-semibold text-gray-700 text-sm mb-3 flex items-center gap-2">
                <Briefcase className="w-4 h-4 text-blue-500" /> Experience
              </h3>
              <div className="space-y-3">
                {cv.experience.slice(0, 4).map((exp, i) => (
                  <div key={i} className="text-sm">
                    <p className="font-medium text-gray-800">{exp.title} {exp.company ? `@ ${exp.company}` : ""}</p>
                    {(exp.startDate || exp.endDate) && (
                      <p className="text-xs text-gray-400">{exp.startDate} – {exp.endDate || "Present"}</p>
                    )}
                    {exp.description && (
                      <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{exp.description}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Education */}
          {cv?.education?.length > 0 && (
            <div className="bg-white rounded-2xl border shadow-sm p-5">
              <h3 className="font-semibold text-gray-700 text-sm mb-3 flex items-center gap-2">
                <GraduationCap className="w-4 h-4 text-purple-500" /> Education
              </h3>
              <div className="space-y-2">
                {cv.education.slice(0, 3).map((edu, i) => (
                  <div key={i} className="text-sm">
                    <p className="font-medium text-gray-800">{edu.degree || edu.institution}</p>
                    {edu.degree && edu.institution && <p className="text-xs text-gray-500">{edu.institution}</p>}
                    {edu.year && <p className="text-xs text-gray-400">{edu.year}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* RIGHT — Questionnaire + Notes */}
        <div className="space-y-4">
          {/* Questionnaire answers */}
          {questionnaireData?.length > 0 ? questionnaireData.map((qd, qi) => (
            <div key={qi} className="bg-white rounded-2xl border shadow-sm p-5">
              <h3 className="font-semibold text-gray-700 text-sm mb-1 flex items-center gap-2">
                <FileText className="w-4 h-4 text-indigo-500" /> {qd.stageLabel}
              </h3>
              <p className="text-xs text-gray-400 mb-4">
                Score: <strong>{qd.score ?? "—"}%</strong> (passing: {qd.passingScore}%)
              </p>
              <div className="space-y-3">
                {qd.answers.map((a, ai) => (
                  <div key={ai} className="border-b last:border-0 pb-3 last:pb-0">
                    <p className="text-xs font-medium text-gray-600 mb-1">Q{ai + 1}. {a.question}</p>
                    {a.questionType === "mcq" ? (
                      <p className="text-sm text-gray-800">
                        Selected option {a.selectedOption !== null ? a.selectedOption + 1 : "—"}
                        <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${a.score > 0 ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                          {a.score > 0 ? "Correct" : "Incorrect"}
                        </span>
                      </p>
                    ) : (
                      <p className="text-sm text-gray-800 bg-gray-50 rounded-lg p-2 leading-relaxed">
                        {a.writtenAnswer || <span className="italic text-gray-400">No answer</span>}
                      </p>
                    )}
                    <p className="text-xs text-gray-400 mt-1">{a.score}/{a.maxScore} pts</p>
                  </div>
                ))}
              </div>
            </div>
          )) : (
            <div className="bg-white rounded-2xl border shadow-sm p-5">
              <h3 className="font-semibold text-gray-700 text-sm mb-2 flex items-center gap-2">
                <FileText className="w-4 h-4 text-indigo-500" /> Questionnaire
              </h3>
              <p className="text-sm text-gray-400 italic">No questionnaire responses yet.</p>
            </div>
          )}

          {/* HR Notes + Decision */}
          <div className="bg-white rounded-2xl border shadow-sm p-5 space-y-4">
            <h3 className="font-semibold text-gray-700 text-sm flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-blue-500" /> HR Notes & Decision
            </h3>

            <div>
              <label className="block text-xs text-gray-500 mb-1.5">Interview notes</label>
              <textarea
                rows={6}
                className="w-full border rounded-xl px-4 py-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-400"
                placeholder="Write your observations from the interview…"
                value={notes}
                onChange={e => { setNotes(e.target.value); setSaved(false); }}
              />
            </div>

            <div>
              <label className="block text-xs text-gray-500 mb-2">Final decision</label>
              <div className="flex gap-2">
                {Object.entries(DECISION_CONFIG).map(([key, cfg]) => {
                  const Icon = cfg.icon;
                  return (
                    <button
                      key={key}
                      onClick={() => { setDecision(key); setSaved(false); }}
                      className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold text-white transition ${
                        decision === key ? cfg.color : "bg-gray-200 text-gray-600 hover:bg-gray-300"
                      }`}
                    >
                      <Icon className="w-4 h-4" /> {cfg.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <button
              onClick={saveDecision}
              disabled={saving || !decision}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 rounded-xl flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {saving ? "Saving…" : saved ? "Update Decision" : "Save Decision"}
            </button>

            {saved && decision && (
              <p className="text-center text-xs text-green-600">
                ✓ Decision "{DECISION_CONFIG[decision]?.label}" saved to candidate's record
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
